import React, { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { RefreshCw, Lock, Power } from 'lucide-react';
import { PxWindow } from '../components/PixelUI';
import { NVLogo } from '../components/Brand';
import { getOwnerChallenge, verifyOwner, getFleet, stopRental, refundRental } from '../api';
import { fmtUsd } from '../catalog';

const ETHERSCAN_TX = 'https://etherscan.io/tx/';

const utf8Hex = (text) =>
  '0x' + Array.from(new TextEncoder().encode(text)).map((b) => b.toString(16).padStart(2, '0')).join('');

const shortHash = (h) => (h ? `${h.slice(0, 8)}…${h.slice(-6)}` : '—');

const TONE = {
  PROVISIONING: 'var(--nv-green)',
  PAID_QUEUED: 'var(--nv-gold)',
  EXPIRED: 'var(--nv-brown)',
  PAID_PROVISION_FAILED: '#ff6b6b',
  REFUNDED: 'var(--nv-green)',
};

const timeLeft = (secs) => {
  if (secs == null) return '—';
  if (secs <= 0) return 'time up';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const StatCard = ({ label, value, accent, testid }) => (
  <div className="nv-fleet-stat" data-testid={testid}>
    <div className="k">{label}</div>
    <div className="v" style={accent ? { color: accent } : undefined}>{value}</div>
  </div>
);

const Unlock = ({ onToken }) => {
  const [busy, setBusy] = useState(false);

  const unlock = async () => {
    if (!window.ethereum) {
      toast.error('No wallet docked — install MetaMask and reload');
      return;
    }
    setBusy(true);
    try {
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      const address = accounts[0];
      const chainId = await window.ethereum.request({ method: 'eth_chainId' });
      if (chainId !== '0x1') {
        await window.ethereum.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: '0x1' }] });
      }
      const message = await getOwnerChallenge();
      const signature = await window.ethereum.request({
        method: 'personal_sign',
        params: [utf8Hex(message), address],
      });
      const token = await verifyOwner(address, message, signature);
      toast.success('Owner verified — console unlocked');
      onToken(token);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'unlock failed';
      toast.error(detail.slice(0, 100));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 460, margin: '0 auto', textAlign: 'center' }}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 22 }}>
        <div style={{ background: 'var(--nv-cream)', border: '3px solid var(--nv-ink)', padding: 10, boxShadow: '5px 5px 0 rgba(0,0,0,.4)' }}>
          <NVLogo size={44} />
        </div>
      </div>
      <PxWindow title="OWNER.LOCK" tape>
        <div style={{ padding: 24, textAlign: 'center' }} data-testid="owner-lock-panel">
          <Lock size={22} style={{ marginBottom: 12 }} />
          <p style={{ fontSize: 13, color: 'var(--nv-brown)', lineHeight: 1.7, margin: '0 0 18px' }}>
            The fleet console is owner-only. Sign a free message with the NANOVRAM treasury
            wallet to prove it's you — nothing sensitive loads until the signature checks out.
          </p>
          <button className="px-btn" onClick={unlock} disabled={busy} data-testid="owner-unlock-btn">
            {busy ? 'WAITING FOR SIGNATURE…' : 'UNLOCK WITH WALLET'}
          </button>
        </div>
      </PxWindow>
    </div>
  );
};

const PodRow = ({ p, onStop, stopping }) => {
  const tone = TONE[p.status] || 'var(--nv-ink)';
  return (
    <tr data-testid={`fleet-pod-${p.order_id}`}>
      <td><span className="nv-sym">{p.die_name}</span></td>
      <td style={{ fontSize: 12 }}>{shortHash(p.wallet)}</td>
      <td style={{ color: tone, fontWeight: 700 }}>{p.pod_ready ? 'RUNNING' : (p.pod_desired_status || p.status)}</td>
      <td>{p.hours}h</td>
      <td style={{ color: p.expired ? 'var(--nv-gold)' : 'var(--nv-green)' }}>{timeLeft(p.seconds_left)}</td>
      <td>{p.total_eth} ETH</td>
      <td>
        <button className="px-btn sm dark" disabled={stopping} onClick={() => onStop(p.order_id)} data-testid={`fleet-stop-${p.order_id}`}>
          <Power size={12} /> {stopping ? '…' : 'STOP'}
        </button>
      </td>
    </tr>
  );
};

const Dashboard = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stoppingId, setStoppingId] = useState(null);
  const [refundingId, setRefundingId] = useState(null);

  const load = useCallback(async (live) => {
    setLoading(true);
    try {
      setData(await getFleet(token, live));
    } catch (err) {
      toast.error(err?.response?.status === 401 ? 'Session expired — unlock again' : 'Could not load the fleet');
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => { load(false); }, [load]);

  const onStop = async (orderId) => {
    setStoppingId(orderId);
    const res = await stopRental(orderId, token);
    if (res) { toast.success('Die stopped'); await load(true); }
    else toast.error('Could not stop the die');
    setStoppingId(null);
  };

  const onRefund = async (r) => {
    if (!window.ethereum) {
      toast.error('No wallet docked — install MetaMask and reload');
      return;
    }
    setRefundingId(r.order_id);
    try {
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      const from = accounts[0];
      const chainId = await window.ethereum.request({ method: 'eth_chainId' });
      if (chainId !== '0x1') {
        await window.ethereum.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: '0x1' }] });
      }
      const valueWei = '0x' + BigInt(Math.round(Number(r.total_eth) * 1e18)).toString(16);
      toast.message(`Sending ${r.total_eth} ETH back to ${shortHash(r.wallet)}… confirm in your wallet`);
      const txHash = await window.ethereum.request({
        method: 'eth_sendTransaction',
        params: [{ from, to: r.wallet, value: valueWei }],
      });
      toast.success('Refund sent on-chain — recording it');
      const res = await refundRental(r.order_id, token, txHash);
      if (res) { toast.success('Refund complete — renter made whole'); await load(false); }
      else toast.error('Refund sent but recording lagged — hit REFUND again once it confirms');
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'refund failed';
      toast.error(detail.slice(0, 120));
    } finally {
      setRefundingId(null);
    }
  };

  if (!data) {
    return <p style={{ textAlign: 'center', color: 'var(--nv-tan)' }}>reading the ledger…</p>;
  }

  const c = data.counts || {};
  return (
    <div data-testid="fleet-dashboard">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <span style={{ fontSize: 12, color: 'var(--nv-tan)', letterSpacing: 1 }} data-testid="fleet-owner">
          owner · {shortHash(data.owner)}
        </span>
        <button className="px-btn sm" onClick={() => load(true)} disabled={loading} data-testid="fleet-refresh-btn">
          <RefreshCw size={13} /> {loading ? 'PULLING LIVE…' : 'REFRESH LIVE'}
        </button>
      </div>

      <div className="nv-fleet-grid" style={{ marginBottom: 30 }}>
        <StatCard label="Revenue (ETH)" value={`${data.revenue_eth} ETH`} accent="var(--nv-green)" testid="fleet-revenue-eth" />
        <StatCard label="Revenue (USD)" value={data.revenue_usd != null ? fmtUsd(data.revenue_usd) : 'no signal'} testid="fleet-revenue-usd" />
        <StatCard label="Active dies" value={c.active ?? 0} accent="var(--nv-gold)" testid="fleet-active-count" />
        <StatCard label="Paid rentals" value={c.paid ?? 0} testid="fleet-paid-count" />
        <StatCard label="ETH quote" value={data.eth_quote ? fmtUsd(data.eth_quote) : 'no signal'} testid="fleet-quote" />
        <StatCard label="Total records" value={c.total ?? 0} testid="fleet-total-count" />
      </div>

      <PxWindow title="ACTIVE.DIES" tape className="nv-tape-term nv-scan">
        <div style={{ overflowX: 'auto' }}>
          <table className="nv-tape-table" data-testid="fleet-active-table">
            <thead>
              <tr><th>Core</th><th>Wallet</th><th>State</th><th>Rented</th><th>Left</th><th>Paid</th><th></th></tr>
            </thead>
            <tbody>
              {(data.active_pods || []).length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 18, opacity: 0.6 }}>no dies running right now</td></tr>
              ) : (
                data.active_pods.map((p) => (
                  <PodRow key={p.order_id} p={p} onStop={onStop} stopping={stoppingId === p.order_id} />
                ))
              )}
            </tbody>
          </table>
        </div>
      </PxWindow>

      <div style={{ height: 26 }} />

      <PxWindow title="ALL.RENTALS" tape className="nv-tape-term nv-scan">
        <div style={{ overflowX: 'auto' }}>
          <table className="nv-tape-table" data-testid="fleet-rentals-table">
            <thead>
              <tr><th>Core</th><th>Wallet</th><th>Status</th><th>ETH</th><th>Tx</th><th>When</th><th></th></tr>
            </thead>
            <tbody>
              {(data.rentals || []).length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 18, opacity: 0.6 }}>no paid rentals yet</td></tr>
              ) : (
                data.rentals.map((r) => (
                  <tr key={r.order_id}>
                    <td><span className="nv-sym">{r.die_name}</span></td>
                    <td style={{ fontSize: 12 }}>{shortHash(r.wallet)}</td>
                    <td style={{ color: TONE[r.status] || 'inherit', fontWeight: 700 }}>{r.status}</td>
                    <td>{r.total_eth}</td>
                    <td style={{ fontSize: 12 }}>
                      {r.tx_hash ? <a href={`${ETHERSCAN_TX}${r.tx_hash}`} target="_blank" rel="noreferrer" style={{ color: 'var(--nv-gold)' }}>{shortHash(r.tx_hash)} ↗</a> : '—'}
                    </td>
                    <td style={{ fontSize: 12 }}>{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                    <td>
                      {r.status === 'PAID_PROVISION_FAILED' && (
                        <button className="px-btn sm dark" disabled={refundingId === r.order_id}
                          title="Send the ETH back with one wallet confirmation"
                          onClick={() => onRefund(r)} data-testid={`fleet-refund-${r.order_id}`}>
                          {refundingId === r.order_id ? '…' : 'SEND REFUND'}
                        </button>
                      )}
                      {r.status === 'REFUNDED' && (
                        <span style={{ fontSize: 11, color: 'var(--nv-green)' }} data-testid={`fleet-refunded-${r.order_id}`}>
                          {r.refund_tx
                            ? <a href={`${ETHERSCAN_TX}${r.refund_tx}`} target="_blank" rel="noreferrer" style={{ color: 'var(--nv-green)' }}>refunded ↗</a>
                            : 'refunded'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </PxWindow>
    </div>
  );
};

const Fleet = () => {
  const [token, setToken] = useState(null);
  return (
    <main className="nv-stars" style={{ minHeight: '85vh' }}>
      <section className="nv-section on-night" style={{ paddingTop: 46, position: 'relative', zIndex: 1 }}>
        <div className="nv-section-inner">
          <p className="nv-eyebrow">owner console · signature-gated, nothing loads until it's you</p>
          <h1 className="nv-h2" style={{ fontSize: 'clamp(26px, 5vw, 44px)' }} data-testid="fleet-page-title">THE FLEET</h1>
          {token ? <Dashboard token={token} /> : <Unlock onToken={setToken} />}
        </div>
      </section>
    </main>
  );
};

export default Fleet;
